import React, { useCallback } from 'react';
import { MapPin } from 'lucide-react';
import type { SiteInfoSection } from '@officehub/shared';
import { INPUT_CLASS, LABEL_CLASS, TEXTAREA_CLASS } from './styles';

export interface SiteInfoFormProps {
  data: SiteInfoSection;
  onUpdate: (d: Partial<SiteInfoSection>) => void;
  /**
   * The survey's ONE site address (surveySiteAddress: resolvedAddress first). This form no longer
   * edits `siteInfo.address`: nothing on the server reads it, so an address typed there never
   * reached a report, and it disagreed with the address on the rest of the survey. Pass
   * `surveySiteAddress(survey)`; the legacy `data.address` is never shown.
   */
  siteAddress?: string | null;
  /**
   * Supply to make the address editable here (the app owns the input, e.g. a typeahead, and saves
   * it as the survey's resolvedAddress). Without it the address is shown read-only.
   */
  renderAddressInput?: (props: { value: string; onChange: (next: string) => void }) => React.ReactNode;
  onAddressChange?: (next: string) => void;
}

export function SiteInfoForm({
  data,
  onUpdate,
  siteAddress,
  renderAddressInput,
  onAddressChange,
}: SiteInfoFormProps): React.ReactElement {
  const shownAddress = siteAddress ?? '';
  // A cleared optional input emits null, never undefined. survey_updateSection
  // merges the fields it is given (issue #3230), so only an explicit null
  // deletes a field — an absent key just leaves the stored value alone. The
  // callable SDK happens to encode undefined as null on the wire, but that is
  // an implementation detail of @firebase/functions; saying null here states
  // the intent at the boundary instead of depending on the transport for it.
  const handleChange = useCallback(
    (field: keyof SiteInfoSection, value: unknown) => {
      onUpdate({ [field]: value });
    },
    [onUpdate],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Section header */}
      <div className="flex items-center gap-2 px-1">
        <MapPin className="w-3.5 h-3.5 text-slate-400" />
        <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">
          Site Information
        </h2>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
      </div>

      {/* Address — full width. The survey's one site address (see siteAddress above). */}
      <div>
        <label className={LABEL_CLASS}>Address</label>
        {renderAddressInput && onAddressChange ? (
          renderAddressInput({ value: shownAddress, onChange: onAddressChange })
        ) : (
          <p data-testid="site-address-readonly" className="px-1 py-2 text-sm text-slate-700">
            {shownAddress || 'No address yet'}
            <span className="block text-xs text-slate-400">The survey address is set on the survey itself (Notes step in the Sales app).</span>
          </p>
        )}
      </div>

      {/* 2-col grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Soil Type */}
        <div>
          <label className={LABEL_CLASS}>Soil Type</label>
          <select
            data-testid="field-soilType"
            className={INPUT_CLASS}
            value={data.soilType ?? ''}
            onChange={(e) =>
              handleChange(
                'soilType',
                e.target.value === '' ? null : (e.target.value as SiteInfoSection['soilType']),
              )
            }
          >
            <option value="">Select…</option>
            <option value="clay">Clay</option>
            <option value="sand">Sand</option>
            <option value="rock">Rock</option>
            <option value="fill">Fill</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>

        {/* Grade */}
        <div>
          <label className={LABEL_CLASS}>Grade</label>
          <select
            data-testid="field-grade"
            className={INPUT_CLASS}
            value={data.grade ?? ''}
            onChange={(e) =>
              handleChange(
                'grade',
                e.target.value === '' ? null : (e.target.value as SiteInfoSection['grade']),
              )
            }
          >
            <option value="">Select…</option>
            <option value="flat">Flat</option>
            <option value="slight_slope">Slight Slope</option>
            <option value="steep">Steep</option>
            <option value="terraced">Terraced</option>
          </select>
        </div>

        {/* Noise Restrictions */}
        <div>
          <label className={LABEL_CLASS}>Noise Restrictions</label>
          <input
            type="text"
            className={INPUT_CLASS}
            value={data.noiseRestrictions ?? ''}
            onChange={(e) => handleChange('noiseRestrictions', e.target.value)}
            placeholder="e.g., HOA limit 55 dB daytime"
          />
        </div>

        {/* HOA Restrictions */}
        <div>
          <label className={LABEL_CLASS}>HOA Restrictions</label>
          <input
            data-testid="field-hoaRestrictions"
            type="text"
            className={INPUT_CLASS}
            value={data.hoaRestrictions ?? ''}
            onChange={(e) => handleChange('hoaRestrictions', e.target.value)}
            placeholder="e.g., Must be screened from street"
          />
        </div>
      </div>

      {/* AHJ Notes — full width */}
      <div>
        <label className={LABEL_CLASS}>AHJ Notes</label>
        <textarea
          className={TEXTAREA_CLASS}
          rows={3}
          value={data.ahjNotes ?? ''}
          onChange={(e) => handleChange('ahjNotes', e.target.value)}
          placeholder="Authority Having Jurisdiction requirements, permit notes…"
        />
      </div>

      {/* Access Notes — full width */}
      <div>
        <label className={LABEL_CLASS}>Access Notes</label>
        <textarea
          className={TEXTAREA_CLASS}
          rows={3}
          value={data.accessNotes ?? ''}
          onChange={(e) => handleChange('accessNotes', e.target.value)}
          placeholder="Gate codes, narrow driveways, key access, HOA contact…"
        />
      </div>
    </div>
  );
}
